import { Info, Check, Heart, Shield, Zap, Users, Package, Calendar, DollarSign, Clock, Mail, FileText, Lock, Eye, Cookie, Camera, ClipboardCheck, PenTool, BarChart3, MapPinned, Image, Code2, Palette, Smartphone, Database, Archive, ShieldCheck, ServerCog, HardDrive, Download, Upload, Trash2, RefreshCcw, Scale, EyeOff, UserCheck, ArrowRightLeft, CircleSlash, Cog, MapPin, XCircle, GitBranch, Monitor, Sparkles, Settings as SettingsIcon, Calculator as CalculatorIcon, MessagesSquare, LifeBuoy } from 'lucide-react';
import { IconTile, TINT } from '../components/ui/IconTile';

// App metadata — describes the SaaS product itself. These values are intentionally
// HARD-CODED and never read from maidly_settings.companyInfo, because that field
// stores the customer's own cleaning-business branding (used on invoices), not the
// app's identity.
const APP_INFO = {
  name: 'Maidly Business Manager',
  version: '1.0.0',
  supportEmail: 'deevelopeer8@gmail.com',
};

const About = () => {

  const features = [
    { icon: ClipboardCheck, title: 'Cleaning Inspections', description: 'Complete inspection system with checklists, photos & reports', tint: TINT.inspections || '#10b981' },
    { icon: Camera, title: 'Live Camera Capture', description: 'Capture photos directly from device camera', tint: '#0ea5e9' },
    { icon: PenTool, title: 'Digital Signatures', description: 'Collect customer signatures on inspections', tint: '#6366f1' },
    { icon: Image, title: 'Before/After Photos', description: 'Visual comparison for quality assurance', tint: '#8b5cf6' },
    { icon: BarChart3, title: 'Analytics Dashboard', description: 'Track inspection trends and performance', tint: '#0073ea' },
    { icon: MapPinned, title: 'GPS Stamping', description: 'Location verification on inspections', tint: TINT.mileage || '#0ea5e9' },
    { icon: Users, title: 'Employee Management', description: 'Manage your team with ease', tint: TINT.employees },
    { icon: Users, title: 'Customer Management', description: 'Track all your clients', tint: TINT.customers },
    { icon: Calendar, title: 'Scheduling', description: 'Smart appointment scheduling', tint: TINT.scheduling },
    { icon: DollarSign, title: 'Invoicing & Quotes', description: 'Professional invoices with PDF export', tint: TINT.invoices },
    { icon: Clock, title: 'Time Tracking', description: 'Track employee hours', tint: TINT.timeClock },
    { icon: Package, title: 'Inventory', description: 'Manage supplies & equipment', tint: TINT.inventory },
  ];

  const capabilities = [
    'Advanced Cleaning Inspection System with customizable checklists',
    'Live camera integration for real-time photo capture',
    'Customer digital signature collection',
    'Before/After photo comparison for quality proof',
    'GPS location stamping on inspections',
    'Analytics dashboard with inspection trends',
    'Professional PDF report generation',
    'Email invoices and inspection reports',
    'Pass All/Fail All bulk actions for checklists',
    'Critical item filtering and status counters',
    'Keyboard shortcuts for faster workflows',
    'Offline functionality with IndexedDB storage',
    'Data compression (70-90% size reduction)',
    'Automatic backup and restore',
    'Recurring appointment scheduling',
    'Multi-device support'
  ];

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '3rem',
        padding: '3rem 1rem',
        background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
        borderRadius: '16px',
        color: '#fff',
        boxShadow: '0 20px 45px rgba(0,115,234,0.28)',
      }}>
        <div style={{ 
          width: '86px', 
          height: '86px', 
          margin: '0 auto 1.5rem',
          background: 'rgba(255, 255, 255, 0.18)',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 20px rgba(0,0,0,0.20)',
        }}>
          <Info size={44} />
        </div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
          {APP_INFO.name}
        </h1>
        <p style={{ fontSize: '1.125rem', opacity: 0.9, marginBottom: '1rem' }}>
          Complete Business Management Solution for Cleaning Professionals
        </p>
        <div style={{ 
          display: 'inline-block',
          padding: '0.5rem 1.15rem',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '999px',
          fontSize: '0.875rem',
          fontWeight: 700,
          letterSpacing: 0.4,
        }}>
          Version {APP_INFO.version}
        </div>
      </div>

      {/* What's Included */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <IconTile icon={Package} tint={TINT.inventory} size={40} />
          Features Included
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.25rem' }}>
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                style={{
                  display: 'flex', gap: '0.85rem',
                  padding: '0.85rem', borderRadius: 12,
                  border: '1px solid #f1f5f9',
                  transition: 'background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = `${feature.tint}0d`;
                  e.currentTarget.style.borderColor = `${feature.tint}66`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${feature.tint}22`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#f1f5f9';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <IconTile icon={Icon} tint={feature.tint} size={44} />
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem', color: '#0f172a' }}>
                    {feature.title}
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.45 }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Capabilities */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <IconTile icon={Zap} tint="#f59e0b" size={40} />
          Capabilities
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0.75rem' }}>
          {capabilities.map((capability) => (
            <div key={capability} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ 
                width: '26px', 
                height: '26px', 
                background: '#dcfce7',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Check size={15} color="#16a34a" strokeWidth={2.75} />
              </div>
              <span style={{ fontSize: '0.9rem', color: '#374151', fontWeight: 500 }}>
                {capability}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Technology Stack */}
      <div
        className="card"
        style={{
          marginBottom: '2rem',
          background:
            'linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #eef2ff 100%)',
          border: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem',
          }}
        >
          <IconTile icon={Shield} tint={TINT.dashboard} size={40} />
          <div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, color: '#0f172a' }}>
              Technology &amp; Security
            </h2>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
              What powers Maidly under the hood.
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1rem',
          }}
        >
          <TechCategoryCard
            title="Frontend"
            tint={TINT.dashboard}
            items={[
              { icon: Code2,      label: 'React 18' },
              { icon: Palette,    label: 'Modern UI/UX' },
              { icon: Smartphone, label: 'Progressive Web App' },
            ]}
          />
          <TechCategoryCard
            title="Storage"
            tint={TINT.mileage}
            items={[
              { icon: Database, label: 'IndexedDB (Compressed)' },
              { icon: HardDrive, label: 'Local Storage' },
              { icon: Archive, label: '70–90% Compression' },
            ]}
          />
          <TechCategoryCard
            title="Security"
            tint={TINT.inspections}
            items={[
              { icon: Lock,        label: 'Encrypted Storage' },
              { icon: ShieldCheck, label: 'Browser Sandbox' },
              { icon: ServerCog,   label: 'Persistent Backup' },
            ]}
          />
        </div>
      </div>

      {/* System Information */}
      <div
        className="card"
        style={{
          marginBottom: '2rem',
          background:
            'linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #eef2ff 100%)',
          border: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem',
          }}
        >
          <IconTile icon={Info} tint={TINT.about} size={40} />
          <div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, color: '#0f172a' }}>
              System Information
            </h2>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
              Build details for {APP_INFO.name}.
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '0.85rem',
          }}
        >
          <SystemFact tint={TINT.about}       icon={Sparkles}   label="Application"  value={APP_INFO.name} />
          <SystemFact tint={TINT.timeClock}   icon={GitBranch}  label="Version"      value={APP_INFO.version} />
          <SystemFact tint={TINT.mileage}     icon={Smartphone} label="Platform"     value="Progressive Web App" />
          <SystemFact tint={TINT.inspections} icon={Monitor}    label="Installation" value="Windows • Mac • Linux • Mobile" />
        </div>
      </div>

      {/* Support & Help */}
      <div
        className="card"
        style={{
          marginBottom: '2rem',
          background:
            'linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #eef2ff 100%)',
          border: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem',
          }}
        >
          <IconTile icon={LifeBuoy} tint={TINT.scheduling} size={40} />
          <div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, color: '#0f172a' }}>
              Support &amp; Help
            </h2>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
              We&apos;re here whenever you need a hand.
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem',
          }}
        >
          {/* Support Email */}
          <a
            href={`mailto:${APP_INFO.supportEmail}`}
            style={{
              textDecoration: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              background: '#ffffffcc',
              backdropFilter: 'blur(6px)',
              border: '1px solid #e5e7eb',
              borderRadius: 16,
              padding: '1.2rem',
              boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 10px 24px ${TINT.scheduling}25`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.04)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: `${TINT.scheduling}1F`,
                  color: TINT.scheduling,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Mail size={18} strokeWidth={2.25} />
              </span>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: 0.7, textTransform: 'uppercase' }}>
                  Support Email
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                  Get in touch
                </div>
              </div>
            </div>
            <div
              style={{
                fontSize: '0.88rem',
                color: TINT.scheduling,
                fontWeight: 600,
                wordBreak: 'break-all',
              }}
            >
              {APP_INFO.supportEmail}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.45 }}>
              Reply times vary, but most emails get a first response within one business day.
            </div>
          </a>

          {/* Common Issues */}
          <div
            style={{
              background: '#ffffffcc',
              backdropFilter: 'blur(6px)',
              border: '1px solid #e5e7eb',
              borderRadius: 16,
              padding: '1.2rem',
              boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.85rem' }}>
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: `${TINT.employees}1F`,
                  color: TINT.employees,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MessagesSquare size={18} strokeWidth={2.25} />
              </span>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: 0.7, textTransform: 'uppercase' }}>
                  Quick Tips
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                  Common Issues
                </div>
              </div>
            </div>
            <IconList
              tint={TINT.employees}
              items={[
                { icon: SettingsIcon,     label: 'Check Settings to configure company info' },
                { icon: Archive,          label: 'Use Storage & Backup for data management' },
                { icon: CalculatorIcon,   label: 'Access Calculator for quick estimates' },
                { icon: Info,             label: 'Review About page for app information' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Legal & Compliance */}
      <div
        className="card"
        style={{
          marginBottom: '2rem',
          background:
            'linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #eef2ff 100%)',
          border: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem',
          }}
        >
          <IconTile icon={Scale} tint={TINT.employees} size={40} />
          <div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, color: '#0f172a' }}>
              Legal &amp; Compliance
            </h2>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
              Your data, your control. Everything you need to know.
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '1rem',
          }}
        >
          {/* Terms of Service */}
          <LegalCard icon={FileText} title="Terms of Service" tint={TINT.dashboard}>
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.75rem' }}>
              Effective {new Date().toLocaleDateString()}
            </div>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', color: '#334155' }}>
              By using {APP_INFO.name}, you agree to these terms:
            </p>
            <BulletList
              tint={TINT.dashboard}
              items={[
                'This software is provided as-is without warranty of any kind',
                'You are responsible for backing up your data regularly',
                'For business use related to cleaning services',
                'Do not misuse or attempt to breach app security',
                'All data remains under your control and ownership',
              ]}
            />
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', marginTop: '0.75rem' }}>
              For complete terms, contact{' '}
              <a href={`mailto:${APP_INFO.supportEmail}`} style={{ color: TINT.dashboard, textDecoration: 'none', fontWeight: 600 }}>
                {APP_INFO.supportEmail}
              </a>
            </div>
          </LegalCard>

          {/* Data Retention */}
          <LegalCard icon={Database} title="Data Retention" tint={TINT.inspections}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', color: '#334155' }}>
              All data lives in your browser (IndexedDB + localStorage). You&apos;re in charge.
            </p>
            <IconList
              tint={TINT.inspections}
              items={[
                { icon: Clock,       label: 'Persists until you delete it' },
                { icon: Archive,     label: 'Back up via Browser Backup' },
                { icon: Trash2,      label: 'Delete all data anytime' },
                { icon: Download,    label: 'Export as JSON' },
                { icon: UserCheck,   label: 'Full control, always yours' },
              ]}
            />
            <Callout tint={TINT.inspections} icon={CircleSlash} label="No Cloud Storage">
              Your data is never sent to external servers. Everything stays on your device.
            </Callout>
          </LegalCard>

          {/* GDPR Compliance */}
          <LegalCard icon={ShieldCheck} title="GDPR Compliance" tint={TINT.calculator}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', color: '#334155' }}>
              Designed with privacy first and aligned to GDPR principles.
            </p>
            <IconList
              tint={TINT.calculator}
              items={[
                { icon: Package,        label: 'Data Minimization — only essentials collected' },
                { icon: UserCheck,      label: 'User Control — you decide access and deletion' },
                { icon: Download,       label: 'Right to Access — export anytime' },
                { icon: XCircle,        label: 'Right to Erasure — delete with one click' },
                { icon: ArrowRightLeft, label: 'Portability — easy import and export' },
                { icon: EyeOff,         label: 'No Third-Party Sharing' },
                { icon: Eye,            label: 'No Behavioral Profiling' },
              ]}
            />
            <Callout tint={TINT.calculator} icon={MapPin} label="Local Processing">
              All processing happens in your browser. No personal data leaves your device.
            </Callout>
          </LegalCard>

          {/* Cookie Policy */}
          <LegalCard icon={Cookie} title="Cookie &amp; Storage Policy" tint={TINT.inventory}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', color: '#334155' }}>
              Browser storage technologies used for essential functionality:
            </p>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              <PolicySubItem
                icon={HardDrive}
                tint={TINT.inventory}
                title="localStorage"
                lines={['App data (customers, invoices, jobs)', 'User preferences and settings']}
              />
              <PolicySubItem
                icon={Database}
                tint={TINT.inventory}
                title="IndexedDB"
                lines={['Compressed backups', 'Offline PWA data']}
              />
              <PolicySubItem
                icon={Cog}
                tint={TINT.inventory}
                title="Service Worker"
                lines={['Caches files for offline use', 'Improves load speed']}
              />
            </div>
            <Callout tint={TINT.inventory} icon={Lock} label="No Tracking Cookies">
              No analytics, advertising, or tracking cookies. Storage is essential-only.
            </Callout>
          </LegalCard>
        </div>
      </div>

      {/* Credits */}
      <div
        className="card"
        style={{
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
          padding: '2.25rem 1.5rem',
          background:
            'linear-gradient(135deg, #0073ea 0%, #005bb5 55%, #1e3a5f 100%)',
          border: '1px solid rgba(255,255,255,0.10)',
          color: '#f8fafc',
        }}
      >
        {/* Decorative glow */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: '-30%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 480,
            height: 480,
            background:
              'radial-gradient(circle, rgba(125,211,252,0.35) 0%, rgba(59,130,246,0.15) 40%, transparent 70%)',
            filter: 'blur(24px)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Heart icon in a gradient chip */}
          <div
            style={{
              width: 62,
              height: 62,
              margin: '0 auto 1rem',
              borderRadius: 18,
              background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 30px rgba(14,165,233,0.45)',
            }}
          >
            <Heart size={30} color="#fff" fill="#fff" />
          </div>

          {/* Tagline pill */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 12px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.10)',
              border: '1px solid rgba(255,255,255,0.20)',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: 1,
              color: '#e0f2fe',
              textTransform: 'uppercase',
              marginBottom: '0.85rem',
            }}
          >
            <Sparkles size={12} /> Made with care
          </div>

          <h3
            style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              margin: 0,
              marginBottom: '0.5rem',
              color: '#ffffff',
              letterSpacing: -0.3,
            }}
          >
            Built for Cleaning Businesses
          </h3>
          <p
            style={{
              fontSize: '0.95rem',
              color: '#dbeafe',
              margin: '0 auto 1.5rem',
              maxWidth: 460,
              lineHeight: 1.55,
            }}
          >
            A comprehensive solution to manage every aspect of your cleaning company —
            from clock-ins to invoices, all in one place.
          </p>

          {/* Divider */}
          <div
            aria-hidden
            style={{
              height: 1,
              width: 120,
              margin: '0 auto 1rem',
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
            }}
          />

          <div
            style={{
              fontSize: '0.78rem',
              color: '#bfdbfe',
              fontWeight: 500,
              letterSpacing: 0.3,
            }}
          >
            © {new Date().getFullYear()} {APP_INFO.name.split(' ')[0]}. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Technology & Security helpers ----

const TechCategoryCard = ({ title, tint, items }) => (
  <div
    style={{
      background: '#ffffffcc',
      backdropFilter: 'blur(6px)',
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      padding: '1rem 1.1rem',
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: '0.75rem',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: tint,
          boxShadow: `0 0 0 3px ${tint}22`,
        }}
      />
      <h3
        style={{
          margin: 0,
          fontSize: '0.78rem',
          fontWeight: 700,
          color: '#475569',
          letterSpacing: 0.7,
          textTransform: 'uppercase',
        }}
      >
        {title}
      </h3>
    </div>
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map(({ icon: Icon, label }) => (
        <li
          key={label}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0.4rem 0',
            fontSize: '0.88rem',
            color: '#0f172a',
            fontWeight: 500,
          }}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: `${tint}1F`,
              color: tint,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={14} strokeWidth={2.25} />
          </span>
          {label}
        </li>
      ))}
    </ul>
  </div>
);

export default About;

// ---- Legal & Compliance helpers ----

const LegalCard = ({ icon: Icon, title, tint, children }) => (
  <div
    style={{
      background: '#ffffffcc',
      backdropFilter: 'blur(6px)',
      border: '1px solid #e5e7eb',
      borderRadius: 16,
      padding: '1.1rem 1.2rem',
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.85rem' }}>
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          background: `${tint}1F`,
          color: tint,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={17} strokeWidth={2.25} />
      </span>
      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{title}</h3>
    </div>
    {children}
  </div>
);

const BulletList = ({ items, tint }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
    {items.map((it) => (
      <li
        key={it}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          padding: '0.35rem 0',
          fontSize: '0.85rem',
          color: '#334155',
          lineHeight: 1.45,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: tint,
            marginTop: 8,
            flexShrink: 0,
          }}
        />
        {it}
      </li>
    ))}
  </ul>
);

const IconList = ({ items, tint }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
    {items.map(({ icon: Icon, label }) => (
      <li
        key={label}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0.4rem 0',
          fontSize: '0.85rem',
          color: '#334155',
        }}
      >
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: 7,
            background: `${tint}1F`,
            color: tint,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={13} strokeWidth={2.25} />
        </span>
        <span>{label}</span>
      </li>
    ))}
  </ul>
);

const Callout = ({ tint, icon: Icon, label, children }) => (
  <div
    style={{
      marginTop: '0.9rem',
      padding: '0.7rem 0.85rem',
      borderRadius: 10,
      background: `${tint}12`,
      border: `1px solid ${tint}40`,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
    }}
  >
    <span
      style={{
        width: 26,
        height: 26,
        borderRadius: 7,
        background: `${tint}25`,
        color: tint,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={14} strokeWidth={2.25} />
    </span>
    <div>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: tint, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.8rem', color: '#334155', lineHeight: 1.45 }}>{children}</div>
    </div>
  </div>
);

const PolicySubItem = ({ icon: Icon, title, lines, tint }) => (
  <div
    style={{
      display: 'flex',
      gap: 10,
      padding: '0.55rem 0.7rem',
      background: '#f8fafc',
      border: '1px solid #eef2f7',
      borderRadius: 10,
    }}
  >
    <span
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `${tint}1F`,
        color: tint,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={14} strokeWidth={2.25} />
    </span>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', marginBottom: 2 }}>
        {title}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {lines.map((l) => (
          <li key={l} style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.4 }}>
            {l}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const SystemFact = ({ icon: Icon, label, value, tint }) => (
  <div
    style={{
      background: '#ffffffcc',
      backdropFilter: 'blur(6px)',
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      padding: '1rem 1.1rem',
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    }}
  >
    <span
      style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: `${tint}1F`,
        color: tint,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={18} strokeWidth={2.25} />
    </span>
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: '0.68rem',
          fontWeight: 700,
          color: '#94a3b8',
          letterSpacing: 0.7,
          textTransform: 'uppercase',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>{value}</div>
    </div>
  </div>
);

